/**
 * Partner Application Form — Progurt Bridge intake
 *
 * Unified practitioner/stockist application. The form posts JSON to the Progurt Bridge
 * webhook (POST https://bridge.progurt.ai/api/webhooks/practitioner-application), guarded
 * by a Cloudflare Turnstile token. The previous third-party forms API is gone.
 *
 * The section renders every named control and the submit button `disabled`, with
 * `onsubmit="return false"` and no action/method, and shows a fallback notice. This file
 * is the only thing that can enable the form, and it does so only after its submit
 * handler is attached, `data-production` is "true", `data-endpoint` is exactly the Bridge
 * URL above, the Turnstile site key is real, and Turnstile has actually rendered. If the
 * Turnstile API never loads the form stays inert. Nothing here logs the payload. A submit
 * that finds no Turnstile token refreshes the widget once and waits for the render
 * callback before it tells the visitor the verification is incomplete.
 *
 * Business type still controls the conditional fields (profession + AHPRA for
 * practitioners only); the honeypot (website_url) is never sent to the Bridge.
 *
 * Wholesale mode (the form carries data-form-mode="wholesale"; the US store page
 * /pages/wholesale): a `?rep=<key>` in the page URL that the Bridge confirms (GET
 * <endpoint>/rep?key=; the section's data-rep-keys list only when the Bridge cannot
 * answer) shows "Your representative: <name>" and adds
 * fields.recruited_by = <key> for the three store business types. An unknown, malformed
 * or missing key does nothing.
 *
 * The US practitioner page (Robert, 28 Oct 2026): a rep recruits practitioners too, so the
 * same ?rep=<key>, confirmed by the Bridge, shows the rep line and adds
 * fields.recruited_by for a practitioner business type as well. It has no rep_keys list.
 * The AU practitioner page never reads the URL and never sends recruited_by.
 *
 * Contracts: the B1 "theme form switch" lane contract (v4) in docs/plans/ of this repo,
 * under the parent B1 application-intake contract (v5.3) in progurt-ops docs/plans/. Their
 * filenames are deliberately not spelled out: these three runtime files must carry no
 * reference to the retired forms provider. tests/practitioner-form.spec.mjs cites both.
 */

(function () {
  'use strict';

  var BRIDGE_ENDPOINT = 'https://bridge.progurt.ai/api/webhooks/practitioner-application';
  var TURNSTILE_CALLBACK = 'pracFormTurnstileReady';
  var TURNSTILE_API_SRC =
    'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=' + TURNSTILE_CALLBACK;
  var TURNSTILE_TIMEOUT_MS = 10000;
  // How long a submit waits for a token after refreshing the widget. An empty token at
  // submit is usually a widget the visitor never saw, or a challenge that expired during
  // a long form fill — not a refusal. Reset once, give Turnstile this long to hand a
  // token to its callback, and only then say the verification is incomplete.
  var TOKEN_WAIT_MS = 12000;
  var SITE_KEY_PLACEHOLDER = 'TURNSTILE_SITE_KEY_PENDING';

  // The Bridge router's field keys, in its order. Anything not in this list — the
  // honeypot above all — never leaves the browser.
  var FIELD_KEYS = [
    'firstname',
    'lastname',
    'email',
    'phone',
    'business_type',
    'company',
    'address',
    'profession',
    'ahpra_number',
    'website',
    'how_heard_about_progurt',
    'message',
  ];

  // One short sentence per router validation code (422). This is the only copy this file
  // inserts: transient, JS-owned, shown in the existing error status block.
  var VALIDATION_MESSAGES = {
    required: 'Please complete the highlighted field.',
    invalid_type: 'The highlighted field needs to be plain text.',
    too_long: 'The highlighted field is too long — please shorten it.',
    control_character: 'Please remove any special characters from the highlighted field.',
    unknown_field: "We can't accept the highlighted field — please clear it.",
    invalid_enum: 'Please pick one of the listed options for the highlighted field.',
    market_origin_mismatch:
      'This form belongs to a different Progurt store — please apply at progurt.com.au or progurt.com.',
    url_invalid: 'Please enter a full web address, starting with https://.',
    email_invalid: 'Please enter a valid email address.',
    phone_invalid: 'Please enter a valid phone number.',
    ahpra_format: 'Please check your AHPRA registration number and try again.',
    registration_format: 'Please check your registration number and try again.',
    field_not_applicable: "This field doesn't apply to the business type you selected — please clear it.",
  };

  var MISSING_TOKEN_MESSAGE = 'Please complete the verification';

  // ── Representative links: wholesale mode and the US practitioner page ──
  // The one extra field key the Bridge accepts, only on a rep-link form and only for the
  // three store business types or a practitioner (never a distributor). It has no control
  // on the page: the asset fills it from a confirmed ?rep= key.
  var REP_FIELD = 'recruited_by';
  var STORE_TYPES = ['pharmacy', 'health_food', 'wellness_center'];
  var REP_TYPES = STORE_TYPES.concat(['practitioner']);
  // The Bridge's rule for a rep key, applied after trim + lower-case.
  var REP_KEY_PATTERN = /^[a-z0-9][a-z0-9_-]{0,31}$/;
  var REP_NAME_MAX = 80;
  // How long a submit waits for the Bridge's rep answer before the rep_keys list decides.
  var REP_LOOKUP_TIMEOUT_MS = 3000;
  // Any 422 whose fields include recruited_by, whatever the code: the rep is dropped, so
  // the next click submits without it. The sentence shows only when no other field is in
  // error; any code without its own sentence uses the recruited_by_unknown one.
  var REP_MESSAGES = {
    field_not_applicable:
      "Your representative's link doesn't apply to this business type. Please submit again without it.",
    recruited_by_unknown: "We couldn't confirm your representative's link. Please submit again without it.",
  };
  var REP_DEFAULT_MESSAGE = REP_MESSAGES.recruited_by_unknown;

  var form = document.getElementById('PractitionerApplicationForm');
  if (!form) return;

  var endpoint = (form.getAttribute('data-endpoint') || '').trim();
  var market = (form.getAttribute('data-market') || '').trim();
  var siteKey = (form.getAttribute('data-turnstile-site-key') || '').trim();
  var isProduction = form.getAttribute('data-production') === 'true';
  var isWholesale = form.getAttribute('data-form-mode') === 'wholesale';
  // Reps work in the US only (Robert, 28 Oct 2026): the wholesale page and the US
  // practitioner page read ?rep=; the AU practitioner page never does.
  var repLinksOn = isWholesale || market === 'us';

  var successEl = form.querySelector('.practitioner-form__status--success');
  var errorEl = form.querySelector('.practitioner-form__status--error');
  var errorParagraph = errorEl ? errorEl.querySelector('p') : null;
  var errorDefaultHtml = errorParagraph ? errorParagraph.innerHTML : '';
  var errorMailto = errorParagraph ? errorParagraph.querySelector('a[href^="mailto:"]') : null;

  var fallbackEl = document.getElementById('PracForm-fallback');
  var turnstileEl = document.getElementById('PracForm-turnstile');
  var submitBtn = form.querySelector('.practitioner-form__submit');

  var businessTypeField = form.querySelector('[name="business_type"]');
  var professionWrap = document.getElementById('PracForm-profession-wrap');
  var ahpraWrap = document.getElementById('PracForm-ahpra-wrap');
  var professionField = form.querySelector('[name="profession"]');
  var ahpraField = form.querySelector('[name="ahpra_number"]');

  // ── Role-conditional banners (P12 visual add) ──
  var rolePracBanner = document.getElementById('PracForm-role-prac');
  var roleTradeBanner = document.getElementById('PracForm-role-trade');
  var TRADE_TYPES = ['pharmacy', 'health_food', 'wellness_center', 'distributor'];

  var formEnabled = false;
  var submitting = false;
  var turnstileWidgetId = null;
  var turnstileTimer = null;
  var turnstileTimedOut = false;

  // The last token Turnstile handed us through its own render callback, and the waiter a
  // submit installs while it waits for one. `tokenWaiter` is non-null only during that
  // bounded wait: a callback arriving after the wait has ended only stores the token, it
  // never submits on its own.
  var callbackToken = '';
  var tokenWaiter = null;

  // ── Conditional fields: show profession + AHPRA only for practitioners ──

  function updateConditionalFields() {
    var isPractitioner = businessTypeField.value === 'practitioner';
    var isTrade = TRADE_TYPES.indexOf(businessTypeField.value) !== -1;
    professionWrap.style.display = isPractitioner ? '' : 'none';
    ahpraWrap.style.display = isPractitioner ? '' : 'none';

    if (rolePracBanner) rolePracBanner.hidden = !isPractitioner;
    if (roleTradeBanner) roleTradeBanner.hidden = !isTrade;

    if (isPractitioner) {
      professionField.setAttribute('required', '');
      professionField.setAttribute('aria-required', 'true');
      ahpraField.setAttribute('required', '');
      ahpraField.setAttribute('aria-required', 'true');
    } else {
      professionField.removeAttribute('required');
      professionField.removeAttribute('aria-required');
      professionField.value = '';
      ahpraField.removeAttribute('required');
      ahpraField.removeAttribute('aria-required');
      ahpraField.value = '';
    }
  }

  // Start hidden, show on practitioner selection
  professionWrap.style.display = 'none';
  ahpraWrap.style.display = 'none';
  businessTypeField.addEventListener('change', updateConditionalFields);

  // ── Representative from ?rep= (wholesale mode and the US practitioner page) ──

  var repLine = document.getElementById('PracForm-rep');
  var repNameEl = document.getElementById('PracForm-rep-name');
  var repKey = '';
  // A submit waits until the rep is settled (Codex review, 27 Oct 2026), so a store that
  // submits quickly still carries its rep.
  var repSettled = true;
  var repWaiters = [];

  function settleRep() {
    if (repSettled) return;
    repSettled = true;
    var waiters = repWaiters;
    repWaiters = [];
    for (var i = 0; i < waiters.length; i++) waiters[i]();
  }

  function afterRep(callback) {
    if (repSettled) callback();
    else repWaiters.push(callback);
  }

  // "dean:Dr Dean Lloyd,jenine:Jenine Stallard" → { dean: 'Dr Dean Lloyd', ... }.
  // Entries with a malformed key or an empty name are skipped.
  function parseRepKeys(text) {
    var reps = {};
    var entries = String(text || '').split(',');
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      var colon = entry.indexOf(':');
      if (colon === -1) continue;
      var key = entry.slice(0, colon).trim().toLowerCase();
      var name = entry.slice(colon + 1).trim();
      if (!REP_KEY_PATTERN.test(key) || !name || name.length > REP_NAME_MAX) continue;
      reps[key] = name;
    }
    return reps;
  }

  function showRep(key, name) {
    repKey = key;
    if (repLine && repNameEl) {
      repNameEl.textContent = name; // text, never markup
      repLine.hidden = false;
    }
  }

  // Robert, 27 Oct 2026: the page asks the Bridge which rep a key names, so a new rep
  // needs no theme edit and a rep who has left stops showing. The Bridge's answer wins:
  // a 404 means no rep. Only when the Bridge cannot answer (offline, 5xx, a bad reply)
  // does the section's rep_keys list decide, as before.
  function resolveRep() {
    if (!repLinksOn) return;
    var raw = null;
    try {
      raw = new URLSearchParams(window.location.search).get('rep');
    } catch (e) {
      return;
    }
    if (!raw) return;
    var key = raw.trim().toLowerCase();
    if (!REP_KEY_PATTERN.test(key)) return;
    var reps = parseRepKeys(form.getAttribute('data-rep-keys'));
    function fromList() {
      if (Object.prototype.hasOwnProperty.call(reps, key)) showRep(key, reps[key]);
    }
    if (!configurationValid() || typeof window.fetch !== 'function') {
      fromList();
      return;
    }
    var NO_REP = {};
    repSettled = false;
    var timer = window.setTimeout(function () {
      if (repSettled) return;
      fromList();
      settleRep();
    }, REP_LOOKUP_TIMEOUT_MS);
    function finish(apply) {
      if (repSettled) return; // a late answer after the timeout is ignored
      window.clearTimeout(timer);
      apply();
      settleRep();
    }
    window
      .fetch(BRIDGE_ENDPOINT + '/rep?key=' + encodeURIComponent(key), {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
      })
      .then(function (response) {
        if (response.status === 404) return NO_REP;
        if (!response.ok) throw new Error('rep lookup unavailable');
        return response.json();
      })
      .then(function (data) {
        if (data === NO_REP) {
          finish(function () {});
          return;
        }
        var name = data && data.key === key && typeof data.display === 'string' ? data.display.trim() : '';
        if (!name || name.length > REP_NAME_MAX) throw new Error('rep lookup reply invalid');
        finish(function () {
          showRep(key, name);
        });
      })
      .catch(function () {
        finish(fromList);
      });
  }

  function dropRep() {
    repKey = '';
    if (repLine) repLine.hidden = true;
  }

  resolveRep();

  // ── Progress strip (P12 visual add) — field-completion proxy, visual only ──
  var segments = form.querySelectorAll('.practitioner-form__seg');
  if (segments.length) {
    var milestones = [
      function () {
        return ['firstname', 'lastname', 'email', 'phone'].every(function (n) {
          var el = form.querySelector('[name="' + n + '"]');
          return el && el.value.trim();
        });
      },
      function () { return !!businessTypeField.value; },
      function () {
        var company = form.querySelector('[name="company"]');
        var address = form.querySelector('[name="address"]');
        return company && address && company.value.trim() && address.value.trim();
      },
      function () { return false; },
    ];
    var refreshProgress = function () {
      var cursor = 0;
      for (var i = 0; i < milestones.length; i++) {
        var done = milestones[i]();
        if (segments[i]) segments[i].classList.toggle('is-done', done);
        if (done) cursor = i + 1;
      }
      for (var j = 0; j < segments.length; j++) {
        segments[j].classList.toggle('is-current', j === cursor);
      }
    };
    form.addEventListener('input', refreshProgress);
    form.addEventListener('change', refreshProgress);
  }

  // ── Status helpers ──

  function clearFieldErrors() {
    var errorFields = form.querySelectorAll('.field--error');
    for (var i = 0; i < errorFields.length; i++) {
      errorFields[i].classList.remove('field--error');
    }
  }

  function showErrorBlock(sentence, withMailto) {
    if (errorParagraph) {
      if (sentence) {
        errorParagraph.textContent = withMailto && errorMailto ? sentence + ' ' : sentence;
        if (withMailto && errorMailto) errorParagraph.appendChild(errorMailto);
      } else {
        errorParagraph.innerHTML = errorDefaultHtml;
        errorMailto = errorParagraph.querySelector('a[href^="mailto:"]');
      }
    }
    if (errorEl) errorEl.hidden = false;
  }

  // The `hidden` attribute only collapses these containers because
  // section-practitioner-form.css carries `.practitioner-form [hidden] { display: none
  // !important; }`. Without it the author `display` rules on `.field` (base.css), the
  // grid and the actions row outrank the user agent's `[hidden]` rule and the form body
  // stays on screen behind the thank-you block (live test 19 Sep 2026, finding F11).
  function hideFormBody() {
    var formEls = form.querySelectorAll('.field, .practitioner-form__actions, .practitioner-form__grid');
    for (var i = 0; i < formEls.length; i++) {
      formEls[i].hidden = true;
    }
    if (turnstileEl) turnstileEl.hidden = true;
  }

  // ── Configuration gate ──

  function configurationValid() {
    return (
      isProduction &&
      endpoint === BRIDGE_ENDPOINT &&
      siteKey !== '' &&
      siteKey !== SITE_KEY_PLACEHOLDER
    );
  }

  function enableForm() {
    var controls = form.querySelectorAll('[name]');
    for (var i = 0; i < controls.length; i++) {
      controls[i].removeAttribute('disabled');
    }
    if (submitBtn) submitBtn.removeAttribute('disabled');
    if (fallbackEl) fallbackEl.hidden = true;
    formEnabled = true;
  }

  // ── Turnstile ──

  function loadTurnstile() {
    // The callback exists before the API script is created, so there is no load race
    // whichever way the browser schedules the injected script.
    window[TURNSTILE_CALLBACK] = onTurnstileReady;

    turnstileTimer = window.setTimeout(function () {
      // The API never loaded, or never called back: leave the form inert behind the
      // fallback notice, and ignore any callback that arrives after the deadline.
      turnstileTimedOut = true;
      turnstileTimer = null;
    }, TURNSTILE_TIMEOUT_MS);

    var script = document.createElement('script');
    script.src = TURNSTILE_API_SRC;
    document.head.appendChild(script);
  }

  // Turnstile hands a solved token to this callback as well as to `getResponse()`.
  // Storing it lets a submit that is waiting for a fresh challenge continue the moment
  // one arrives, instead of dying on the empty token it started with.
  function onTurnstileToken(token) {
    callbackToken = typeof token === 'string' ? token : '';
    if (!tokenWaiter) return;
    var waiter = tokenWaiter;
    tokenWaiter = null;
    if (waiter.timer) window.clearTimeout(waiter.timer);
    waiter.resolve(callbackToken);
  }

  // Expiry and widget errors clear the stored token. They never end a wait early: a
  // challenge that errors is treated exactly like one that never answered, so the wait
  // runs its course and the visitor keeps the full window to complete the widget.
  function onTurnstileTokenCleared() {
    callbackToken = '';
  }

  function onTurnstileReady() {
    if (turnstileTimer) {
      window.clearTimeout(turnstileTimer);
      turnstileTimer = null;
    }
    if (turnstileTimedOut || formEnabled) return;
    if (!turnstileEl || !window.turnstile || typeof window.turnstile.render !== 'function') return;

    var widgetId;
    try {
      widgetId = window.turnstile.render(turnstileEl, {
        sitekey: siteKey,
        callback: onTurnstileToken,
        'expired-callback': onTurnstileTokenCleared,
        'error-callback': onTurnstileTokenCleared,
      });
    } catch (e) {
      return;
    }
    if (widgetId === null || widgetId === undefined) return;

    turnstileWidgetId = widgetId;
    enableForm();
  }

  function turnstileToken() {
    if (turnstileWidgetId === null || !window.turnstile || typeof window.turnstile.getResponse !== 'function') {
      return '';
    }
    try {
      return window.turnstile.getResponse(turnstileWidgetId) || '';
    } catch (e) {
      return '';
    }
  }

  function resetTurnstile() {
    // A reset invalidates whatever the previous challenge produced.
    callbackToken = '';
    if (turnstileWidgetId === null || !window.turnstile || typeof window.turnstile.reset !== 'function') return;
    try {
      window.turnstile.reset(turnstileWidgetId);
    } catch (e) {
      // A widget that cannot be reset simply leaves the visitor to reload the page.
    }
  }

  // Resolves with the token Turnstile delivers to its callback, or with '' when nothing
  // arrives inside TOKEN_WAIT_MS. Exactly one waiter exists at a time: the in-flight
  // guard in onSubmit keeps a second click from installing another.
  function waitForTurnstileToken() {
    return new Promise(function (resolve) {
      var waiter = { resolve: resolve, timer: null };
      waiter.timer = window.setTimeout(function () {
        if (tokenWaiter === waiter) tokenWaiter = null;
        resolve('');
      }, TOKEN_WAIT_MS);
      tokenWaiter = waiter;
    });
  }

  function removeTurnstile() {
    callbackToken = '';
    if (turnstileWidgetId === null || !window.turnstile || typeof window.turnstile.remove !== 'function') return;
    try {
      window.turnstile.remove(turnstileWidgetId);
    } catch (e) {
      // Nothing to recover: the application was already accepted.
    }
    turnstileWidgetId = null;
  }

  // ── Validation ──

  function validateRequired() {
    var requiredFields = form.querySelectorAll('[required]');
    var valid = true;
    for (var i = 0; i < requiredFields.length; i++) {
      if (!requiredFields[i].value.trim()) {
        var parentField = requiredFields[i].closest('.field');
        if (parentField && parentField.style.display !== 'none') {
          parentField.classList.add('field--error');
          valid = false;
        }
      }
    }

    // Basic email validation
    var emailField = form.querySelector('[name="email"]');
    if (emailField && emailField.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailField.value)) {
      emailField.closest('.field').classList.add('field--error');
      valid = false;
    }

    return valid;
  }

  // ── Request / response ──

  function buildBody(token) {
    var fields = {};
    for (var i = 0; i < FIELD_KEYS.length; i++) {
      var key = FIELD_KEYS[i];
      var control = form.querySelector('[name="' + key + '"]');
      if (!control) continue;
      var value = (control.value || '').trim();
      if (value) fields[key] = value; // empty optionals are omitted
    }
    // A rep-link form only, confirmed rep only, store or practitioner business types only.
    if (repLinksOn && repKey && REP_TYPES.indexOf(fields.business_type) !== -1) {
      fields[REP_FIELD] = repKey;
    }
    return {
      market: market,
      page: window.location.href,
      turnstile_token: token,
      fields: fields,
    };
  }

  function readResponse(response) {
    return response.text().then(
      function (text) {
        return { status: response.status, text: text };
      },
      function () {
        return { status: response.status, text: '' };
      }
    );
  }

  function parseJson(text) {
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch (e) {
      return null;
    }
  }

  function codeOf(value) {
    if (typeof value === 'string') return value;
    if (value && typeof value.code === 'string') return value.code;
    return '';
  }

  // True when every key the router returned is one of the twelve DOM field names and its
  // control is on the page — i.e. the 422 is entirely about fields the visitor can fix
  // here. Any other key (market, page, turnstile_token, body, fields, anything unknown)
  // makes this false, and the transport mailto stays in the block. An empty map is not a
  // field problem either. A rep-link form: recruited_by counts as fixable here too — the
  // asset drops the rep and the next click submits without it.
  function allKeysAreFields(fields) {
    var keys = Object.keys(fields);
    if (keys.length === 0) return false;
    for (var i = 0; i < keys.length; i++) {
      if (repLinksOn && keys[i] === REP_FIELD) continue;
      if (FIELD_KEYS.indexOf(keys[i]) === -1) return false;
      if (!form.querySelector('[name="' + keys[i] + '"]')) return false;
    }
    return true;
  }

  // Highlights every returned key that is one of the twelve DOM field names and returns
  // the sentence for the first of them. Keys that are not DOM field names (market, page,
  // turnstile_token, body, fields, anything unknown) are left to the generic block.
  function applyFieldErrors(fields) {
    var sentence = null;
    var keys = Object.keys(fields);
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if (FIELD_KEYS.indexOf(key) === -1) continue;
      var control = form.querySelector('[name="' + key + '"]');
      if (!control) continue;
      var wrapper = control.closest('.field');
      if (wrapper) wrapper.classList.add('field--error');
      if (!sentence) sentence = VALIDATION_MESSAGES[codeOf(fields[key])] || null;
    }
    return sentence;
  }

  function succeed() {
    submitting = false;
    removeTurnstile();
    hideFormBody();
    if (successEl) {
      successEl.hidden = false;
      successEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // `fieldOnly` is true only when the failure is entirely about form fields the visitor
  // can correct here. Then the block carries the field sentence alone: appending the
  // partners@ mailto to a single bad optional field reads as a failure of the form
  // rather than of that field (live test 19 Sep 2026, finding F4). Transport failures,
  // non-field keys and verification failures keep the mailto.
  function failSubmission(sentence, originalText, fieldOnly) {
    submitting = false;
    if (submitBtn) {
      submitBtn.textContent = originalText;
      submitBtn.disabled = false;
    }
    showErrorBlock(sentence, !fieldOnly);
    resetTurnstile();
  }

  function handleResponse(result, originalText) {
    var parsed = parseJson(result.text);

    if (
      result.status === 200 &&
      parsed &&
      parsed.received === true &&
      typeof parsed.application_id === 'string' &&
      parsed.application_id.length > 0
    ) {
      succeed();
      return;
    }

    var sentence = null;
    var fieldOnly = false;
    if (result.status === 422 && parsed && parsed.fields && typeof parsed.fields === 'object') {
      sentence = applyFieldErrors(parsed.fields);
      if (repLinksOn && Object.prototype.hasOwnProperty.call(parsed.fields, REP_FIELD)) {
        dropRep();
        // The rep sentence only when no DOM field is in error; otherwise the field's own
        // sentence (or the generic block for a field code we cannot name) stands.
        if (!form.querySelector('.field--error')) {
          var repCode = codeOf(parsed.fields[REP_FIELD]);
          sentence = Object.prototype.hasOwnProperty.call(REP_MESSAGES, repCode)
            ? REP_MESSAGES[repCode]
            : REP_DEFAULT_MESSAGE;
        }
      }
      // A sentence we could not name (an unrecognised code) falls back to the generic
      // block with the mailto, even when every key was a field.
      fieldOnly = sentence !== null && allKeysAreFields(parsed.fields);
    }
    failSubmission(sentence, originalText, fieldOnly);
  }

  // ── Submission ──

  function postApplication(token, originalText) {
    submitting = true;
    if (submitBtn) {
      submitBtn.textContent = 'Submitting...';
      submitBtn.disabled = true;
    }
    afterRep(function () {
      sendApplication(buildBody(token), originalText);
    });
  }

  function sendApplication(body, originalText) {
    fetch(endpoint, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(readResponse)
      .then(function (result) {
        handleResponse(result, originalText);
      })
      .catch(function () {
        failSubmission(null, originalText, false);
      });
  }

  function onSubmit(e) {
    e.preventDefault();
    if (!formEnabled || submitting) return;

    successEl.hidden = true;
    errorEl.hidden = true;
    clearFieldErrors();

    // Honeypot check — if filled, silently "succeed" (bot doesn't know)
    var honeypot = form.querySelector('[name="website_url"]');
    if (honeypot && honeypot.value) {
      hideFormBody();
      successEl.hidden = false;
      return;
    }

    if (!validateRequired()) {
      var firstError = form.querySelector('.field--error .field__input');
      if (firstError) firstError.focus();
      return;
    }

    var originalText = submitBtn ? submitBtn.textContent : '';

    var token = turnstileToken();
    if (token) {
      postApplication(token, originalText);
      return;
    }

    // No token yet. Before stage 1's fix round this was the end of the road: the visitor
    // saw MISSING_TOKEN_MESSAGE with no way to act, because Dawn's `div:empty` rule hid
    // the widget itself (live acceptance test 20 Sep 2026, finding F12). The widget is
    // visible now; give it a chance to produce a token before saying anything. One reset
    // asks Cloudflare for a fresh challenge — it either passes the visitor silently and
    // calls back, or draws the checkbox they can now see and calls back when they solve
    // it. The in-flight guard stays up for the whole wait, so a second click is ignored
    // and the widget is reset exactly once.
    submitting = true;
    if (submitBtn) submitBtn.disabled = true;
    resetTurnstile();

    waitForTurnstileToken().then(function (delivered) {
      var fresh = delivered || turnstileToken();
      submitting = false;
      if (submitBtn) submitBtn.disabled = false;
      if (!fresh) {
        // The wait ended with nothing. The widget stays on screen for the visitor to
        // complete, and the next click submits.
        showErrorBlock(MISSING_TOKEN_MESSAGE, false);
        return;
      }
      postApplication(fresh, originalText);
    });
  }

  // The handler is attached before anything can enable the form.
  form.addEventListener('submit', onSubmit);

  if (configurationValid()) {
    loadTurnstile();
  }
})();
